import { ThemeEvents } from '@theme/events';

class CartIcon extends HTMLElement {
  constructor() {
    super();
    this.cartCount = null;
    this.addToCartBehavior = null;

    this.clickHandler = this.handleClick.bind(this);
    this.cartUpdatedHandler = this.onCartUpdated.bind(this);
    this.pageShowHandler = this.syncCartCountFromServer.bind(this);
    this.focusHandler = this.syncCartCountFromServer.bind(this);
    this.visibilityHandler = () => { if (!document.hidden) this.syncCartCountFromServer(); };
    this.syncTimer = null;
    this.syncGeneration = 0;
  }

  connectedCallback() {
    this.addToCartBehavior = this.dataset.addToCartBehavior || 'open_cart';

    this.cartCount = this.querySelector('[data-ref="cart-count"]');

    this.addEventListener('click', this.clickHandler);
    document.addEventListener(ThemeEvents.cartUpdated, this.cartUpdatedHandler);
    window.addEventListener('pageshow', this.pageShowHandler);
    window.addEventListener('focus', this.focusHandler);
    document.addEventListener('visibilitychange', this.visibilityHandler);
    this.syncCartCountFromServer();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdated, this.cartUpdatedHandler);
    window.removeEventListener('pageshow', this.pageShowHandler);
    window.removeEventListener('focus', this.focusHandler);
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    clearTimeout(this.syncTimer);
    this.syncGeneration++;
    this.removeEventListener('click', this.clickHandler);
  }

  getCurrentItemCount() {
    return parseInt(this.cartCount.textContent);
  }

  onCartUpdated(event) {
    const { itemCount, source } = event.detail?.data || {};

    // An earlier /cart.js request (e.g. when opening the drawer) must never
    // overwrite the count returned by a more recent successful cart mutation.
    this.syncGeneration++;

    if (Number.isInteger(itemCount) && itemCount >= 0) {
      this.#setCount(itemCount);
    }

    // Removing/changing an item in the drawer must not reopen the drawer.
    if (this.addToCartBehavior === 'open_cart' &&
        source !== 'cart-reservation-timer' &&
        source !== 'cart-drawer-item') {
      this.openCart();
    }

    clearTimeout(this.syncTimer);
    // Reconcile with Shopify after the cart mutation and drawer rendering settle.
    this.syncTimer = setTimeout(() => this.syncCartCountFromServer(), 700);
  }

  setCount(count) {
    this.syncGeneration++;
    this.#setCount(count);
  }

  #setCount(count) {
    // Header sections can be replaced without reinitializing this component.
    // Always update the LIVE badge nodes, not a stale cached cartCount reference.
    const numericCount = Number(count);
    if (!Number.isInteger(numericCount) || numericCount < 0) return;

    document.querySelectorAll('cart-icon').forEach((icon) => {
      const liveCount = icon.querySelector('[data-ref="cart-count"]');
      const bubble = icon.querySelector('.header__icon-count-bubble');
      if (liveCount) {
        liveCount.textContent = String(numericCount);
        if (icon === this) this.cartCount = liveCount;
      }
      if (bubble) {
        bubble.hidden = numericCount === 0;
        bubble.style.setProperty('display', numericCount === 0 ? 'none' : '', numericCount === 0 ? 'important' : '');
        if (numericCount !== 0) bubble.style.removeProperty('display');
        bubble.classList.toggle('header__icon-count-bubble--long', numericCount >= 100);
      }
    });
  }

  async syncCartCountFromServer() {
    const generation = ++this.syncGeneration;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart.js?_edukid_sync=' + Date.now(), {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) return;
      const cart = await response.json();
      if (generation !== this.syncGeneration || !this.isConnected) return;
      if (Number.isInteger(cart.item_count) && cart.item_count >= 0) {
        this.#setCount(cart.item_count);
      }
    } catch (error) {
      console.warn('Cart count sync failed:', error);
    }
  }

  ensureCartCountIsCorrect() {
    this.syncCartCountFromServer();
  }

  handleClick() {
    this.openCart();
  }

  openCart() {
    const cartDrawer = document.querySelector('[data-ref="cart-drawer"]');
    if (cartDrawer) {
      cartDrawer.open();
    } else {
      window.location.href = Theme.routes.cart_url;
    }
  }
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
